import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Card,
  Layout,
  Text,
  Badge,
  Button,
  Icon,
  BlockStack,
  InlineStack,
  TextContainer,
  Modal,
  TextField,
  Select,
  ButtonGroup,
  Checkbox,
  Tooltip,
  Spinner,
  Autocomplete,
} from '@shopify/polaris';
import {
  CircleTickMajor,
  ContentMinor,
  EditMajor,
  DeleteMajor,
  AddMajor,
  ChevronLeftMinor,
  ChevronRightMinor,
  SearchMinor,
} from '@shopify/polaris-icons';
import { BlobServiceClient } from '../services/blobStorageCompat';
import '@shopify/polaris/build/esm/styles.css';
import '../modal-overrides.css';
import '../dashboard.css';

import { scenesService } from '../services/scenesService';
import { storiesService } from '../services/storiesService';
import AuthenticatedAssetImage from './AuthenticatedAssetImage';

// Define SERVER_URL constant
const SERVER_URL = process.env.REACT_APP_SERVER_URL;

if (!SERVER_URL) {
  console.error('REACT_APP_SERVER_URL environment variable is not set');
}

const ScenesPage = ({ setToastMessage, setToastActive, setIsCreateSceneModalOpen, isCreateSceneModalOpen }) => {
    // Table headings
    const headings = [
      'Image',
      'Story',
      'Slug',
      'Type',
      'Text',
      'Status',
      'Actions',
    ];

    const [scenesTab, setScenesTab] = useState('all');
    const [scenes, setScenes] = useState([]);
    const [deletedScenes, setDeletedScenes] = useState([]);
    const [scenesSearchQuery, setScenesSearchQuery] = useState('');
    const [stories, setStories] = useState([]);
    const [isLoadingScenes, setIsLoadingScenes] = useState(false);
    const [isLoadingDeletedScenes, setIsLoadingDeletedScenes] = useState(false);
    const [selectedScene, setSelectedScene] = useState(null);
    const [isSceneModalOpen, setIsSceneModalOpen] = useState(false);
    const [isEditSceneModalOpen, setIsEditSceneModalOpen] = useState(false);
    const [isDeleteSceneModalOpen, setIsDeleteSceneModalOpen] = useState(false);
    const [isRestoreSceneModalOpen, setIsRestoreSceneModalOpen] = useState(false);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState('');
    const [selectedImageAlt, setSelectedImageAlt] = useState('');
    const [isCreatingScene, setIsCreatingScene] = useState(false);
    const [isDeletingScene, setIsDeletingScene] = useState(false);
    const [isRestoringScene, setIsRestoringScene] = useState(false);
    const [deleteSceneId, setDeleteSceneId] = useState('');
    const [restoreSceneId, setRestoreSceneId] = useState('');

    // 2. Add state for Create Scene modal
    const [storyAutocompleteInputValue, setStoryAutocompleteInputValue] = useState('');
    const [storyAutocompleteOptions, setStoryAutocompleteOptions] = useState([]); // [{label, value}]
    const [selectedStoryForScene, setSelectedStoryForScene] = useState([]); // array of string

    // 3. Add state for Edit Scene modal
    const [editStoryAutocompleteInputValue, setEditStoryAutocompleteInputValue] = useState('');
    const [editStoryAutocompleteOptions, setEditStoryAutocompleteOptions] = useState([]);
    const [selectedStoryForEditScene, setSelectedStoryForEditScene] = useState([]);

    // Add state for Previewer Story Autocomplete
    const [isLoadingStories, setIsLoadingStories] = useState(false);


    const [newSceneData, setNewSceneData] = useState({
      slug: '',
      sceneTitle: '',
      order: '',
      type: '',
      scene_url: '',
      mask_url: '',
      text: '',
      scene_prompt: '',
      blurred: false,
      story_id: '',
      demo_text: ''
    });
    const [editSceneData, setEditSceneData] = useState({
      slug: '',
      sceneTitle: '',
      order: '',
      type: '',
      scene_url: '',
      mask_url: '',
      text: '',
      scene_prompt: '',
      blurred: false,
      story_id: '',
      demo_text: ''
    });
  
    // Pagination for scenes
    const [scenesPage, setScenesPage] = useState(1);
    const SCENES_PER_PAGE = 50;
    const paginatedScenes = (
      scenesTab === 'all' ? scenes :
      scenesTab === 'deleted' ? deletedScenes :
      scenesTab === 'blurred' ? scenes.filter(scene => scene.blurred) :
      scenesTab === 'clear' ? scenes.filter(scene => !scene.blurred) :
      scenes
    ).filter(scene => {
      const q = scenesSearchQuery.toLowerCase();
      return (
        (scene.text && scene.text.toLowerCase().includes(q)) ||
        (scene.type && scene.type.toLowerCase().includes(q)) ||
        (scene.slug && scene.slug.toLowerCase().includes(q)) ||
        (scene.story_id && stories.find(s => s._id === scene.story_id)?.title.toLowerCase().includes(q))
      );
    });
    const totalScenesPages = Math.ceil(paginatedScenes.length / SCENES_PER_PAGE);
    const scenesToShow = paginatedScenes.slice((scenesPage - 1) * SCENES_PER_PAGE, scenesPage * SCENES_PER_PAGE);

    // Helper function to generate slug from title
    const generateSlug = (title) => {
        if (!title) return '';
        return title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
        .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
    };

    // Helper function to determine which fields should be shown based on scene type
    const getSceneTypeFields = (sceneType) => {
        switch (sceneType) {
        case 'cover':
            return {
            showText: true,
            showMaskUrl: true,
            showBlurred: false,
            showDemoText: true,
            showPrompt: true,
            textRequired: true,
            maskUrlRequired: true,
            demoTextRequired: true
            };
        case 'image':
            return {
            showText: false,
            showMaskUrl: true,
            showBlurred: false,
            showDemoText: false,
            showPrompt: true,
            textRequired: false,
            maskUrlRequired: true,
            demoTextRequired: false
            };
        case 'text':
            return {
            showText: true,
            showMaskUrl: false,
            showBlurred: false,
            showDemoText: false,
            showPrompt: false,
            textRequired: true,
            maskUrlRequired: false,
            demoTextRequired: false
            };
        case 'static':
            return {
            showText: false,
            showMaskUrl: false,
            showBlurred: true,
            showDemoText: false,
            showPrompt: false,
            textRequired: false,
            maskUrlRequired: false,
            demoTextRequired: false
            };
        case 'back':
            return {
            showText: false,
            showMaskUrl: false,
            showBlurred: false,
            showDemoText: false,
            showPrompt: false,
            textRequired: false,
            maskUrlRequired: false,
            demoTextRequired: false
            };
        default:
            return {
            showText: false,
            showMaskUrl: false,
            showBlurred: false,
            showDemoText: false,
            showPrompt: false,
            textRequired: false,
            maskUrlRequired: false,
            demoTextRequired: false
            };
        }
    };

    // Helper function to prepare scene data based on scene type
    const prepareSceneData = (sceneData) => {
        const baseData = {
        slug: sceneData.slug,
        order: sceneData.order,
        type: sceneData.type,
        scene_url: sceneData.scene_url,
        story_id: sceneData.story_id
        };

        switch (sceneData.type) {
        case 'cover':
            return {
            ...baseData,
            mask_url: sceneData.mask_url,
            text: sceneData.text,
            demo_text: sceneData.demo_text,
            scene_prompt: sceneData.scene_prompt
            };
        case 'image':
            return {
            ...baseData,
            mask_url: sceneData.mask_url,
            scene_prompt: sceneData.scene_prompt
            };
        case 'text':
            return {
            ...baseData,
            text: sceneData.text
            };
        case 'static':
            return {
            ...baseData,
            blurred: sceneData.blurred
            };
        case 'back':
            return {
            ...baseData
            };
        default:
            return baseData;
        }
    };

      // Stories functions
    const fetchStories = useCallback(async () => {
        setIsLoadingStories(true);
        try {
        const data = await storiesService.getAllStories();
        const storiesArray = Array.isArray(data) ? data : [];
        setStories(storiesArray);
        } catch (err) {
        console.error('Fetch error:', err);
        setStories([]); // Ensure stories is always an array
        } finally {
        setIsLoadingStories(false);
        }
    }, []);
    
    // Scenes CRUD functions
    const fetchScenes = useCallback(async () => {
        setIsLoadingScenes(true);
        try {
        const data = await scenesService.getAllScenes();
        setScenes(Array.isArray(data) ? data : []);
        } catch (err) {
        console.error('Fetch scenes error:', err);
        setScenes([]);
        } finally {
        setIsLoadingScenes(false);
        }
    }, []);

    const fetchDeletedScenes = useCallback(async () => {
        setIsLoadingDeletedScenes(true);
        try {
        const data = await scenesService.getDeletedScenes();
        setDeletedScenes(Array.isArray(data) ? data : []);
        } catch (err) {
        console.error('Fetch deleted scenes error:', err);
        setDeletedScenes([]);
        } finally {
        setIsLoadingDeletedScenes(false);
        }
    }, []);
    

    const handleCreateScene = useCallback(async () => {
        if (!newSceneData.slug || !newSceneData.type || !newSceneData.story_id || !newSceneData.scene_url) {
        setToastMessage('Please fill in all required fields');
        setToastActive(true);
        setTimeout(() => {
            setToastActive(false);
        }, 3000);
        return;
        }

        // Validate type-specific required fields
        const typeFields = getSceneTypeFields(newSceneData.type);
        if (typeFields.textRequired && !newSceneData.text) {
        setToastMessage('Scene Text is required for this scene type');
        setToastActive(true);
        setTimeout(() => {
            setToastActive(false);
        }, 3000);
        return;
        }
        if (typeFields.maskUrlRequired && !newSceneData.mask_url) {
        setToastMessage('Mask URL is required for this scene type');
        setToastActive(true);
        setTimeout(() => {
            setToastActive(false);
        }, 3000);
        return;
        }
        if (typeFields.demoTextRequired && !newSceneData.demo_text) {
        setToastMessage('Demo Text is required for this scene type');
        setToastActive(true);
        setTimeout(() => {
            setToastActive(false);
        }, 3000);
        return;
        }

        setIsCreatingScene(true);
        try {
        const preparedData = prepareSceneData(newSceneData);
        console.log('Sending scene data:', preparedData);
        await scenesService.createScene(preparedData);
        setToastMessage('Scene Created Successfully');
        setToastActive(true);
        setTimeout(() => {
            setToastActive(false);
        }, 3000);
        fetchScenes();
        setNewSceneData({
            slug: '', sceneTitle: '', order: '', type: '', scene_url: '', mask_url: '', text: '', scene_prompt: '', blurred: false, story_id: '', demo_text: ''
        });
        setIsCreateSceneModalOpen(false);
        } catch (err) {
        setToastMessage(`Error: ${err.message || 'Failed to create scene'}`);
        setToastActive(true);
        setTimeout(() => {
            setToastActive(false);
        }, 5000);
        } finally {
        setIsCreatingScene(false);
        }
    }, [newSceneData, fetchScenes]);

    const handleUpdateScene = useCallback(async () => {
        if (!editSceneData.slug || !editSceneData.type || !editSceneData.story_id || !editSceneData.scene_url || !selectedScene) {
        setToastMessage('Please fill in all required fields');
        setToastActive(true);
        setTimeout(() => {
            setToastActive(false);
        }, 3000);
        return;
        }

        // Validate type-specific required fields
        const typeFields = getSceneTypeFields(editSceneData.type);
        if (typeFields.textRequired && !editSceneData.text) {
        setToastMessage('Scene Text is required for this scene type');
        setToastActive(true);
        setTimeout(() => {
            setToastActive(false);
        }, 3000);
        return;
        }
        if (typeFields.maskUrlRequired && !editSceneData.mask_url) {
        setToastMessage('Mask URL is required for this scene type');
        setToastActive(true);
        setTimeout(() => {
            setToastActive(false);
        }, 3000);
        return;
        }
        if (typeFields.demoTextRequired && !editSceneData.demo_text) {
        setToastMessage('Demo Text is required for this scene type');
        setToastActive(true);
        setTimeout(() => {
            setToastActive(false);
        }, 3000);
        return;
        }

        try {
        const preparedData = prepareSceneData(editSceneData);
        console.log('Updating previewer scene data:', preparedData);
        await scenesService.updateScene(selectedScene._id, preparedData);
        setToastMessage('Scene Updated Successfully');
        setToastActive(true);
        setTimeout(() => {
            setToastActive(false);
        }, 3000);
        fetchScenes();
        setIsEditSceneModalOpen(false);
        setSelectedScene(null);
        } catch (err) {
        setToastMessage(`Error: ${err.message || 'Failed to update scene'}`);
        setToastActive(true);
        setTimeout(() => {
            setToastActive(false);
        }, 5000);
        }
    }, [editSceneData, selectedScene, fetchScenes]);

    const handleDeleteScene = useCallback((id) => {
        setDeleteSceneId(id);
        setIsDeleteSceneModalOpen(true);
    }, []);

    const handleConfirmDeleteScene = useCallback(async () => {
        if (!deleteSceneId) return;
        setIsDeletingScene(true);
        try {
        await scenesService.deleteScene(deleteSceneId);
        setToastMessage('Scene Deleted Successfully');
        setToastActive(true);
        setTimeout(() => {
            setToastActive(false);
        }, 3000);
        fetchScenes();
        setIsDeleteSceneModalOpen(false);
        setDeleteSceneId('');
        } catch (err) {
        setToastMessage(`Error: ${err.message || 'Failed to delete scene'}`);
        setToastActive(true);
        setTimeout(() => {
            setToastActive(false);
        }, 5000);
        } finally {
        setIsDeletingScene(false);
        }
    }, [deleteSceneId, fetchScenes]);

    const handleRestoreScene = useCallback((id) => {
        setRestoreSceneId(id);
        setIsRestoreSceneModalOpen(true);
    }, []);

    const handleConfirmRestoreScene = useCallback(async () => {
        if (!restoreSceneId) return;
        setIsRestoringScene(true);
        try {
        await scenesService.restoreScene(restoreSceneId);
        setToastMessage('Scene Restored Successfully');
        setToastActive(true);
        setTimeout(() => {
            setToastActive(false);
        }, 3000);
        fetchDeletedScenes();
        fetchScenes();
        setIsRestoreSceneModalOpen(false);
        setRestoreSceneId('');
        } catch (err) {
        setToastMessage(`Error: ${err.message || 'Failed to restore scene'}`);
        setToastActive(true);
        setTimeout(() => {
            setToastActive(false);
        }, 5000);
        } finally {
        setIsRestoringScene(false);
        }
    }, [restoreSceneId, fetchDeletedScenes, fetchScenes]);

    const handleImageClick = useCallback((imageUrl, imageAlt) => {
        setSelectedImageUrl(imageUrl);
        setSelectedImageAlt(imageAlt);
        setIsImageModalOpen(true);
    }, []);

    // 5. Handlers for Create Scene modal
    const handleStoryAutocompleteChange = useCallback((value) => {
        console.log('Story autocomplete change:', value);
        setStoryAutocompleteInputValue(value);
        if (value === '') {
        const allOptions = stories.map(story => ({ label: story.title, value: story._id }));
        console.log('Setting all options:', allOptions);
        setStoryAutocompleteOptions(allOptions);
        return;
        }
        const filterRegex = new RegExp(value, 'i');
        const filteredOptions = stories
        .filter(story => story.title.match(filterRegex) || story.slug.match(filterRegex))
        .map(story => ({ label: story.title, value: story._id }));
        console.log('Setting filtered options:', filteredOptions);
        setStoryAutocompleteOptions(filteredOptions);
    }, [stories]);

    const handleStoryAutocompleteSelect = useCallback((selected) => {
        console.log('Story autocomplete select:', selected);
        setSelectedStoryForScene(selected);
        const selectedId = selected[0] || '';
        setNewSceneData(prev => ({ ...prev, story_id: selectedId }));
        const selectedStory = stories.find(story => story._id === selectedId);
        setStoryAutocompleteInputValue(selectedStory ? selectedStory.title : '');
    }, [stories]);

    // 6. Handlers for Edit Scene modal
    const handleEditStoryAutocompleteChange = useCallback((value) => {
        console.log('Edit story autocomplete change:', value);
        setEditStoryAutocompleteInputValue(value);
        if (value === '') {
        const allOptions = stories.map(story => ({ label: story.title, value: story._id }));
        console.log('Setting edit all options:', allOptions);
        setEditStoryAutocompleteOptions(allOptions);
        return;
        }
        const filterRegex = new RegExp(value, 'i');
        const filteredOptions = stories
        .filter(story => story.title.match(filterRegex) || story.slug.match(filterRegex))
        .map(story => ({ label: story.title, value: story._id }));
        console.log('Setting edit filtered options:', filteredOptions);
        setEditStoryAutocompleteOptions(filteredOptions);
    }, [stories]);

    const handleEditStoryAutocompleteSelect = useCallback((selected) => {
        console.log('Edit story autocomplete select:', selected);
        setSelectedStoryForEditScene(selected);
        const selectedId = selected[0] || '';
        setEditSceneData(prev => ({ ...prev, story_id: selectedId }));
        const selectedStory = stories.find(story => story._id === selectedId);
        setEditStoryAutocompleteInputValue(selectedStory ? selectedStory.title : '');
    }, [stories]);

    // 7. When opening Edit Scene modal, set initial autocomplete state
    const handleEditScene = useCallback((scene) => {
        setEditSceneData({ ...scene, sceneTitle: scene.sceneTitle || scene.slug.replace(/-/g, ' ') });
        setSelectedScene(scene);
        setSelectedStoryForEditScene(scene.story_id ? [scene.story_id] : []);
        const selectedStory = stories.find(s => s._id === scene.story_id);
        setEditStoryAutocompleteInputValue(selectedStory ? selectedStory.title : '');
        setIsEditSceneModalOpen(true);
    }, [stories]);

    // 4. Update options when stories change
    useEffect(() => {
        console.log('Stories changed:', stories);
        const options = stories.map(story => ({ label: story.title, value: story._id }));
        console.log('Autocomplete options:', options);
        setStoryAutocompleteOptions(options);
        setEditStoryAutocompleteOptions(options);
        
    }, [stories]);


    useEffect(() => {
        fetchStories(); // Always fetch stories when entering Scenes tab
        if (scenesTab === 'all') {
            fetchScenes();
        } else if (scenesTab === 'deleted') {
            fetchDeletedScenes();
        }
    }, [scenesTab, fetchScenes, fetchDeletedScenes, fetchStories]);

    useEffect(() => {
        fetchScenes();
    }, []);

    // Render each scene as a row
    return (
      <Layout style={{ marginBottom: '24px' }}>

        <Layout.Section>
          <Card>
            {/* Filter/Status Tabs and Search Bar Row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 0 16px' }}>
              {/* Tabs */}
              <div style={{ display: 'flex', gap: 8 }}>
                <ButtonGroup>
                  <Button 
                    onClick={() => setScenesTab('all')}
                    variant="secondary"
                    tone={scenesTab === 'all' ? 'success' : undefined}
                  >
                    All
                  </Button>
                  <Button 
                    onClick={() => setScenesTab('blurred')}
                    variant="secondary"
                    tone={scenesTab === 'blurred' ? 'success' : undefined}
                  >
                    Blurred
                  </Button>
                  <Button 
                    onClick={() => setScenesTab('clear')}
                    variant="secondary"
                    tone={scenesTab === 'clear' ? 'success' : undefined}
                  >
                    Clear
                  </Button>
                  <Button 
                    onClick={() => setScenesTab('deleted')}
                    variant="secondary"
                    tone={scenesTab === 'deleted' ? 'success' : undefined}
                  >
                    Deleted
                  </Button>
                </ButtonGroup>
              </div>
              {/* Search Bar */}
              {(scenesTab === 'all' ? scenes : deletedScenes).length > 0 && (
                <div style={{ minWidth: 260 }}>
                  <TextField
                    label="Search by title, type, slug, or story..."
                    value={scenesSearchQuery}
                    onChange={setScenesSearchQuery}
                    placeholder="Search by title, type, slug, or story..."
                    autoComplete="off"
                    prefix={
                      <span className="Polaris-Icon">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="9" cy="9" r="7.5" stroke="var(--s-icon-color-25041, rgba(74, 74, 74, 1))" strokeWidth="1.5" />
                          <path d="M15 15L18 18" stroke="var(--s-icon-color-25041, rgba(74, 74, 74, 1))" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </span>
                    }
                    labelHidden
                  />
                </div>
              )}
            </div>
            <div style={{ overflowX: 'auto', marginTop: 16 }}>
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                {(scenesTab === 'all' ? scenes : deletedScenes).filter(scene => {
                  const q = scenesSearchQuery.toLowerCase();
                  return (
                    (scene.text && scene.text.toLowerCase().includes(q)) ||
                    (scene.type && scene.type.toLowerCase().includes(q)) ||
                    (scene.slug && scene.slug.toLowerCase().includes(q)) ||
                    (scene.story_id && stories.find(s => s._id === scene.story_id)?.title.toLowerCase().includes(q))
                  );
                }).length > 0 && (
                  <thead>
                    <tr>
                      {headings.map((heading, idx) => (
                        <th key={idx} style={{
                          textAlign: 'center',
                          padding: '12px 8px',
                          fontWeight: 600,
                          color: '#6d7175',
                          fontSize: 13,
                          background: '#fafbfc',
                          borderBottom: '1px solid #e3e3e3',
                          minWidth: idx === 0 ? 56 : 80,
                        }}>{heading}</th>
                      ))}
                    </tr>
                  </thead>
                )}
                <tbody>
                  {isLoadingScenes || isLoadingDeletedScenes ? (
                    <tr>
                      <td colSpan="7" style={{ padding: '60px 16px', textAlign: 'center' }}>
                        <BlockStack gap="400" align="center">
                          <Spinner size="large" />
                          <Text variant="bodyMd" tone="subdued">Loading scenes...</Text>
                        </BlockStack>
                      </td>
                    </tr>
                  ) : (scenesTab === 'all' ? scenes : deletedScenes).filter(scene => {
                    const q = scenesSearchQuery.toLowerCase();
                    return (
                      (scene.text && scene.text.toLowerCase().includes(q)) ||
                      (scene.type && scene.type.toLowerCase().includes(q)) ||
                      (scene.slug && scene.slug.toLowerCase().includes(q)) ||
                      (scene.story_id && stories.find(s => s._id === scene.story_id)?.title.toLowerCase().includes(q))
                    );
                  }).length > 0 ? (
                    scenesToShow.map(scene => {
                      const storyTitle = stories.find(s => s._id === scene.story_id)?.title || 'Unknown Story';
                      const sceneImageUrl = scene.scene_url || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-image.png';
                      return (
                        <tr key={scene._id || scene.slug} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>{/* Image */}
                            <div 
                              style={{ 
                                width: 48, 
                                height: 48, 
                                borderRadius: 8, 
                                overflow: 'hidden', 
                                background: '#f6f6f7', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                cursor: 'pointer',
                                margin: '0 auto',
                                transition: 'transform 0.2s ease-in-out'
                              }}
                              onClick={() => handleImageClick(
                                sceneImageUrl,
                                scene.text
                              )}
                              onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                            >
                              <AuthenticatedAssetImage
                                src={sceneImageUrl} 
                                assetSize="thumbnail"
                                alt={scene.text} 
                                style={{ width: 48, height: 48, objectFit: 'cover' }} 
                              />
                            </div>
                          </td>
                          <td style={{ padding: '12px 8px', minWidth: 100, color: '#6d7175', fontSize: 13, textAlign: 'center' }}>{storyTitle}</td>
                          <td style={{ padding: '12px 8px', minWidth: 120, color: '#6d7175', fontSize: 13, textAlign: 'center' }}>{scene.slug && scene.slug.trim() ? scene.slug : ''}</td>
                          <td style={{ padding: '12px 8px', minWidth: 100, color: '#6d7175', fontSize: 13, textAlign: 'center' }}>{scene.type && scene.type.trim() ? scene.type : ''}</td>
                          <td style={{ padding: '12px 8px', maxWidth: 90, textAlign: 'center' }}>
                            <div>
                              {scene.text && scene.text.trim() ? (
                                <Text variant="bodyMd">{scene.text}</Text>
                              ) : null}
                            </div>
                          </td>
                          <td style={{ padding: '12px 8px', minWidth: 120, textAlign: 'center' }}>
                            {scene.blurred ? (
                              <Badge tone="warning">Blurred</Badge>
                            ) : (
                              <Badge tone="success">Clear</Badge>
                            )}
                          </td>
                          <td style={{ padding: '12px 8px', minWidth: 120, textAlign: 'center' }}>
                            <InlineStack gap="100" align="center">
                              {scenesTab === 'all' ? (
                                <>
                                  <Tooltip content="Edit">
                                    <Button 
                                      icon={EditMajor} 
                                      size="slim" 
                                      onClick={() => handleEditScene(scene)}
                                    />
                                  </Tooltip>
                                  <Tooltip content="Delete">
                                    <Button 
                                      icon={DeleteMajor} 
                                      size="slim" 
                                      onClick={() => handleDeleteScene(scene._id)}
                                    />
                                  </Tooltip>
                                </>
                              ) : (
                                <Tooltip content="Restore">
                                  <Button 
                                    icon={CircleTickMajor} 
                                    size="slim" 
                                    onClick={() => handleRestoreScene(scene._id)}
                                  />
                                </Tooltip>
                              )}
                            </InlineStack>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ padding: '60px 16px', textAlign: 'center' }}>
                        <BlockStack gap="400" align="center">
                          <Icon 
                            source={ContentMinor} 
                            color="subdued" 
                            className="empty-state-big-icon"
                          />
                          <Text variant="bodyMd" tone="subdued">No records found</Text>
                          {scenesTab === 'all' && (
                            <div style={{ display: 'inline-block' }}>
                              <Button 
                                icon={AddMajor} 
                                onClick={() => setIsCreateSceneModalOpen(true)}
                                variant="primary"
                                size="large"
                              >
                                Add Scene
                              </Button>
                            </div>
                          )}
                        </BlockStack>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Segmented Pagination Controls */}
            {totalScenesPages > 1 && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '24px 0' }}>
                <ButtonGroup variant="segmented">
                  <Button
                    icon={ChevronLeftMinor}
                    disabled={scenesPage === 1}
                    onClick={() => setScenesPage(scenesPage - 1)}
                  />
                  {(() => {
                    // Show up to 5 page numbers, with ellipsis if needed
                    let start = Math.max(1, scenesPage - 2);
                    let end = Math.min(totalScenesPages, start + 4);
                    if (end - start < 4) start = Math.max(1, end - 4);
                    const pages = [];
                    for (let i = start; i <= end; i++) {
                      pages.push(
                        <Button
                          key={i}
                          pressed={i === scenesPage}
                          onClick={() => setScenesPage(i)}
                          variant={i === scenesPage ? 'secondary' : 'secondary'}
                        >
                          {i}
                        </Button>
                      );
                    }
                    return pages;
                  })()}
                  <Button
                    icon={ChevronRightMinor}
                    disabled={scenesPage === totalScenesPages}
                    onClick={() => setScenesPage(scenesPage + 1)}
                  />
                </ButtonGroup>
                <div style={{ marginTop: 8, marginBottom: 16, color: '#6d7175', fontSize: 13 }}>
                  {((scenesPage - 1) * SCENES_PER_PAGE) + 1}
                  –
                  {Math.min(scenesPage * SCENES_PER_PAGE, paginatedScenes.length)}
                  {' of '}
                  {paginatedScenes.length}
                  {' (Page '}{scenesPage}{' of '}{totalScenesPages}{')'}
                </div>
              </div>
            )}
          </Card>
        </Layout.Section>

        {/* Scene Details Modal */}
        {selectedScene && (
          <Modal
            open={isSceneModalOpen}
            onClose={() => setIsSceneModalOpen(false)}
            title={selectedScene.text}
            primaryAction={{
              content: 'Close',
              onAction: () => setIsSceneModalOpen(false),
            }}
          >
            <BlockStack gap="400">
              <Text variant="bodyMd">{selectedScene.text}</Text>
              <InlineStack gap="200">
                <Badge>{selectedScene.type}</Badge>
                <Badge>{selectedScene.story_id ? stories.find(s => s._id === selectedScene.story_id)?.title : 'No Story'}</Badge>
              </InlineStack>
            </BlockStack>
          </Modal>
        )}

        {/* Create Scene Modal */}
        <Modal
          open={isCreateSceneModalOpen}
          onClose={() => setIsCreateSceneModalOpen(false)}
          title={<div style={{padding: '16px'}}>{'Create New Scene'}</div>}
          primaryAction={{
            content: 'Create',
            onAction: handleCreateScene,
            loading: isCreatingScene,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => setIsCreateSceneModalOpen(false),
            },
          ]}
          footer={<div style={{padding: '16px'}}></div>}
        >
          <BlockStack gap="400">
            <TextField
              label="Scene Title"
              value={newSceneData.sceneTitle}
              onChange={(value) => setNewSceneData(prev => ({ ...prev, sceneTitle: value, slug: generateSlug(value) }))}
              placeholder="Scene title"
              required
            />
            <TextField
              label="Scene Slug"
              value={newSceneData.slug}
              onChange={() => {}}
              placeholder="scene-slug"
              required
              disabled
            />
            <TextField
              label="Order"
              type="number"
              value={newSceneData.order}
              onChange={(value) => setNewSceneData(prev => ({ ...prev, order: parseInt(value) || 0 }))}
              placeholder="0"
              min="0"
              required
            />
            <Select
              label="Scene Type"
              options={[
                { label: 'Select Scene Type', value: '', disabled: true },
                { label: 'Cover', value: 'cover' },
                { label: 'Image', value: 'image' },
                { label: 'Text', value: 'text' },
                { label: 'Static', value: 'static' },
                { label: 'Back', value: 'back' },
              ]}
              onChange={(value) => setNewSceneData(prev => ({ ...prev, type: value }))}
              value={newSceneData.type}
              required
            />
            <Autocomplete
              options={storyAutocompleteOptions}
              selected={selectedStoryForScene}
              onSelect={handleStoryAutocompleteSelect}

              loading={isLoadingStories}
              textField={
                <Autocomplete.TextField
                  onChange={handleStoryAutocompleteChange}
                  label="Story"
                  value={storyAutocompleteInputValue}
                  prefix={<Icon source={SearchMinor} tone="base" />}
                  placeholder="Search for a story..."
                  autoComplete="off"
                  required
                />
              }
            />
            {console.log('Create Scene Autocomplete props:', {
              options: storyAutocompleteOptions,
              selected: selectedStoryForScene,
              loading: isLoadingStories
            })}
            <TextField
              label="Scene URL"
              value={newSceneData.scene_url}
              onChange={(value) => setNewSceneData(prev => ({ ...prev, scene_url: value }))}
              placeholder="https://example.com/scene.png"
              required
            />
            {getSceneTypeFields(newSceneData.type).showPrompt && (
              <TextField
                label="Scene Prompt"
                value={newSceneData.scene_prompt}
                onChange={(value) => setNewSceneData(prev => ({ ...prev, scene_prompt: value }))}
                placeholder="Prompt used to generate this scene..."
                multiline={4}
                helpText="Optional. Stored as multiline string in database."
              />
            )}
            {getSceneTypeFields(newSceneData.type).showText && (
              <div>
                <TextField
                  label="Scene Text"
                  value={newSceneData.text}
                  onChange={(value) => setNewSceneData(prev => ({ ...prev, text: value }))}
                  placeholder="Scene text..."
                  multiline={2}
                  required
                />
                <Text variant="bodySm" tone="subdued" style={{ marginTop: '4px' }}>
                  Use {'{{Kid_Name}}'} for Kid name variable
                </Text>
              </div>
            )}
            {getSceneTypeFields(newSceneData.type).showMaskUrl && (
              <TextField
                label="Mask URL"
                value={newSceneData.mask_url}
                onChange={(value) => setNewSceneData(prev => ({ ...prev, mask_url: value }))}
                placeholder="https://example.com/mask.png"
                required
              />
            )}
            {getSceneTypeFields(newSceneData.type).showBlurred && (
              <Checkbox
                label="Blurred"
                checked={newSceneData.blurred}
                onChange={(checked) => setNewSceneData(prev => ({ ...prev, blurred: checked }))}
                required
              />
            )}
            {getSceneTypeFields(newSceneData.type).showDemoText && (
              <TextField
                label="Demo Text"
                value={newSceneData.demo_text}
                onChange={(value) => setNewSceneData(prev => ({ ...prev, demo_text: value }))}
                placeholder="Demo text for this scene"
                required
              />
            )}
          </BlockStack>
        </Modal>

        {/* Edit Scene Modal */}
        {selectedScene && (
          <Modal
            open={isEditSceneModalOpen}
            onClose={() => setIsEditSceneModalOpen(false)}
            title={<div style={{padding: '16px'}}>{'Edit Scene'}</div>}
            primaryAction={{
              content: 'Save',
              onAction: handleUpdateScene,
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: () => setIsEditSceneModalOpen(false),
              },
            ]}
            footer={<div style={{padding: '16px'}}></div>}
          >
            <BlockStack gap="400">
              <TextField
                label="Scene Title"
                value={editSceneData.sceneTitle}
                onChange={(value) => setEditSceneData(prev => ({ ...prev, sceneTitle: value, slug: generateSlug(value) }))}
                placeholder="Scene title"
                required
              />
              <TextField
                label="Scene Slug"
                value={editSceneData.slug}
                onChange={() => {}}
                required
                disabled
              />
              <TextField
                label="Order"
                type="number"
                value={editSceneData.order}
                onChange={(value) => setEditSceneData(prev => ({ ...prev, order: parseInt(value) || 0 }))}
                placeholder="0"
                min="0"
                required
              />
              <Select
                label="Scene Type"
                options={[
                  { label: 'Select Scene Type', value: '', disabled: true },
                  { label: 'Cover', value: 'cover' },
                  { label: 'Image', value: 'image' },
                  { label: 'Text', value: 'text' },
                  { label: 'Static', value: 'static' },
                  { label: 'Back', value: 'back' },
                ]}
                onChange={(value) => setEditSceneData(prev => ({ ...prev, type: value }))}
                value={editSceneData.type}
                required
              />
              <Autocomplete
                options={editStoryAutocompleteOptions}
                selected={selectedStoryForEditScene}
                onSelect={handleEditStoryAutocompleteSelect}

                loading={isLoadingStories}
                textField={
                  <Autocomplete.TextField
                    onChange={handleEditStoryAutocompleteChange}
                    label="Story"
                    value={editStoryAutocompleteInputValue}
                    prefix={<Icon source={SearchMinor} tone="base" />}
                    placeholder="Search for a story..."
                    autoComplete="off"
                    required
                  />
                }
              />
              {console.log('Edit Scene Autocomplete props:', {
                options: editStoryAutocompleteOptions,
                selected: selectedStoryForEditScene,
                loading: isLoadingStories
              })}
              <TextField
                label="Scene URL"
                value={editSceneData.scene_url}
                onChange={(value) => setEditSceneData(prev => ({ ...prev, scene_url: value }))}
                placeholder="https://example.com/scene.png"
                required
              />
              {getSceneTypeFields(editSceneData.type).showPrompt && (
                <TextField
                  label="Scene Prompt"
                  value={editSceneData.scene_prompt}
                  onChange={(value) => setEditSceneData(prev => ({ ...prev, scene_prompt: value }))}
                  placeholder="Prompt used to generate this scene..."
                  multiline={4}
                  helpText="Optional. Stored as multiline string in database."
                />
              )}
              {getSceneTypeFields(editSceneData.type).showText && (
                <div>
                  <TextField
                    label="Scene Text"
                    value={editSceneData.text}
                    onChange={(value) => setEditSceneData(prev => ({ ...prev, text: value }))}
                    placeholder="Scene text..."
                    multiline={2}
                    required
                  />
                  <Text variant="bodySm" tone="subdued" style={{ marginTop: '4px' }}>
                    Use {'{{Kid_Name}}'} for Kid name variable
                  </Text>
                </div>
              )}
              {getSceneTypeFields(editSceneData.type).showMaskUrl && (
                <TextField
                  label="Mask URL"
                  value={editSceneData.mask_url}
                  onChange={(value) => setEditSceneData(prev => ({ ...prev, mask_url: value }))}
                  placeholder="https://example.com/mask.png"
                  required
                />
              )}
              {getSceneTypeFields(editSceneData.type).showBlurred && (
                <Checkbox
                  label="Blurred"
                  checked={editSceneData.blurred}
                  onChange={(checked) => setEditSceneData(prev => ({ ...prev, blurred: checked }))}
                  required
                />
              )}
              {getSceneTypeFields(editSceneData.type).showDemoText && (
                <TextField
                  label="Demo Text"
                  value={editSceneData.demo_text}
                  onChange={(value) => setEditSceneData(prev => ({ ...prev, demo_text: value }))}
                  placeholder="Demo text for this scene"
                  required
                />
              )}
            </BlockStack>
          </Modal>
        )}

        {/* Delete Confirmation Modal */}
        <Modal
          open={isDeleteSceneModalOpen}
          onClose={() => setIsDeleteSceneModalOpen(false)}
          title={<div style={{padding: '16px'}}>{'Confirm Deletion'}</div>}
          primaryAction={{
            content: 'Delete',
            onAction: handleConfirmDeleteScene,
            loading: isDeletingScene,
            destructive: true,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => setIsDeleteSceneModalOpen(false),
            },
          ]}
          footer={<div style={{padding: '16px'}}></div>}
        >
          <BlockStack gap="400">
            <Text>Are you sure you want to delete this scene? This action cannot be undone.</Text>
          </BlockStack>
        </Modal>

        {/* Restore Confirmation Modal */}
        <Modal
          open={isRestoreSceneModalOpen}
          onClose={() => setIsRestoreSceneModalOpen(false)}
          title={<div style={{padding: '16px'}}>{'Confirm Restore'}</div>}
          primaryAction={{
            content: 'Restore',
            onAction: handleConfirmRestoreScene,
            loading: isRestoringScene,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => setIsRestoreSceneModalOpen(false),
            },
          ]}
          footer={<div style={{padding: '16px'}}></div>}
        >
          <BlockStack gap="400">
            <Text>Are you sure you want to restore this scene? This action cannot be undone.</Text>
          </BlockStack>
        </Modal>

        {/* Image Modal */}
        {isImageModalOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              zIndex: 9999,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '20px'
            }}
            onClick={() => setIsImageModalOpen(false)}
          >
            <div
              style={{
                position: 'relative',
                maxWidth: '600px',
                maxHeight: '500px',
                width: '90%',
                height: 'auto',
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '20px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <AuthenticatedAssetImage
                src={selectedImageUrl} 
                assetSize="preview"
                alt={selectedImageAlt}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: 'inherit', 
                  objectFit: 'contain',
                  borderRadius: '4px'
                }} 
              />
              <button
                onClick={() => setIsImageModalOpen(false)}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  zIndex: 1000,
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
              >
                ×
              </button>
            </div>
          </div>
        )}
      </Layout>
    );
};

export default ScenesPage;